import { useState } from 'react'
import { useActor } from '@/auth/pin/phoneSession'
import { useFormat, useI18n } from '@/i18n'
import { formatPaise } from '@/lib/money'
import { enqueueCommand } from '@/offline'
import { Banner, BilingualLabel, Button, Card, CardBody, CardHeader, Field, IntegerInput, PhotoCapture, useToast } from '@/ui'
import { liveOutputs, runParentIds, type PackRunView } from '@/features/process/projection'
import { Line, YesNo } from '@/features/process/components/bits'
import { useBoth } from '@/features/process/components/useText'
import type { PackClosePayload } from '../../commands'
import '../../commands'
import { reconcile } from '../../model/reconcile'
import type { FillDraft, FillUpdate } from './fillDraft'

/**
 * F7.4 — Close run on the phone: the last pouch against the approved label, codes printed from
 * the coder counter (with its photo), spoiled and destroyed, and the reconciliation
 * printed = applied + spoiled + destroyed. A gap after a recount still closes; the lot then
 * waits for the quality person.
 */
export function CloseSection({
  run,
  fill,
  update,
  onClosed,
}: {
  run: PackRunView
  fill: FillDraft
  update: FillUpdate
  onClosed: () => void
}) {
  const { t } = useI18n()
  const fmt = useFormat()
  const toast = useToast()
  const actor = useActor()
  const both = useBoth()
  const label = run.label_versions.find((l) => l.id === run.label_version_id) ?? run.label_versions[0]
  const [labelOk, setLabelOk] = useState<boolean | null>(null)
  const [printed, setPrinted] = useState<number | null>(null)
  const [spoiled, setSpoiled] = useState<number | null>(null)
  const [destroyed, setDestroyed] = useState<number | null>(null)
  const [photo, setPhoto] = useState<string | null>(null)
  const [recounted, setRecounted] = useState(false)
  const [saving, setSaving] = useState(false)
  const rec =
    printed !== null && spoiled !== null && destroyed !== null
      ? reconcile({ printed, applied: fill.pouches_good, spoiled, destroyed })
      : null
  const ready = labelOk !== null && !!photo && !!rec && (rec.ok || recounted)
  const inputG = run.inputs.reduce((a, i) => a + (i.weight_g ?? 0), 0)
  const outs = liveOutputs(run)
  const leftoverG = outs.filter((o) => o.stream_key === 'LEFTOVER').reduce((a, o) => a + o.net_g, 0)
  const spillageG = outs.filter((o) => o.stream_key === 'SPILLAGE').reduce((a, o) => a + o.net_g, 0)

  const close = async () => {
    if (!ready || !rec || !photo || labelOk === null || !run.pack_lot) return
    setSaving(true)
    try {
      const payload: PackClosePayload = {
        run_id: run.id,
        pack_lot_id: run.pack_lot.id,
        pouches_good: fill.pouches_good,
        pouches_rejected: fill.pouches_rejected,
        labels_printed: rec.printed,
        labels_spoiled: rec.spoiled,
        labels_destroyed: rec.destroyed,
        last_pouch_label_ok: labelOk,
        counter_photo_id: photo,
        phone_totals: { input_g: inputG, leftover_g: leftoverG, spillage_g: spillageG, reconciliation_gap: rec.gap },
      }
      await enqueueCommand({
        type: 'pack_close',
        payload,
        actorId: actor.user_id,
        mediaIds: [photo],
        parentIds: runParentIds(run),
      })
      toast.success(t('pack.close.done'))
      onClosed()
    } catch (e) {
      toast.error(t('process.saveFailed'), e instanceof Error ? e.message : undefined)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader eyebrow={<BilingualLabel k="pack.close.label" />} title={<span className="font-mono">{label?.code ?? '—'}</span>} />
        <CardBody className="py-1">
          {run.pack_lot && <Line label={t('pack.close.lotCode')} value={run.pack_lot.code} />}
          {run.pack_lot && <Line label={t('pack.coder.packDate')} value={fmt.date(run.pack_lot.pack_date)} />}
          {run.pack_lot && <Line label={t('pack.coder.bestBefore')} value={fmt.date(run.pack_lot.best_before)} />}
          {label?.mrp_paise != null && <Line label={t('pack.label.mrp')} value={formatPaise(label.mrp_paise)} />}
          {label?.allergen_statement && (
            <div className="py-2 text-[14px]">
              <div className="text-muted">{t('pack.label.allergen')}</div>
              <div className="font-bold">{label.allergen_statement}</div>
            </div>
          )}
        </CardBody>
      </Card>
      <Field label={<BilingualLabel k="pack.close.lastMatches" />} required>
        <YesNo value={labelOk} onChange={setLabelOk} />
      </Field>
      {labelOk === false && <Banner tone="warn">{t('pack.close.lastNoMatch')}</Banner>}
      {(!run.first_pouch?.match || !run.last_pouch?.match) && <Banner tone="warn">{t('pack.close.pouchCheckGap')}</Banner>}

      <Card>
        <CardHeader eyebrow={<BilingualLabel k="pack.close.counts" />} />
        <CardBody className="flex flex-col gap-3">
          <Line label={<BilingualLabel k="pack.close.applied" />} value={fill.pouches_good} strong />
          <Field label={<BilingualLabel k="pack.close.printed" />} required>
            <IntegerInput value={printed} onChange={setPrinted} />
          </Field>
          <Field label={<BilingualLabel k="pack.close.counterPhoto" />} required>
            <PhotoCapture value={photo} onChange={setPhoto} />
          </Field>
          <Field label={<BilingualLabel k="pack.close.spoiled" />} hint={t('pack.close.spoiledHint', { n: fill.pouches_rejected })} required>
            <IntegerInput value={spoiled} onChange={setSpoiled} />
          </Field>
          <Field label={<BilingualLabel k="pack.close.destroyed" />} hint={t('pack.close.destroyedHint')} required>
            <IntegerInput value={destroyed} onChange={setDestroyed} />
          </Field>
        </CardBody>
      </Card>

      {rec && (
        <Card className={rec.ok ? 'border-ok' : 'border-warn'}>
          <CardHeader eyebrow={<BilingualLabel k="pack.close.reconciliation" />} />
          <CardBody className="py-1">
            <p className="py-2 text-center font-mono text-[16px] tabular">
              {rec.printed} = {rec.applied} + {rec.spoiled} + {rec.destroyed}
            </p>
            {rec.ok ? (
              <p className="pb-2 text-center font-bold text-ok">{t('pack.close.addsUp')}</p>
            ) : (
              <>
                <p className="pb-2 text-center font-bold text-warn">{both('pack.close.gap', { n: rec.gap })}</p>
                <Banner tone="warn">{t('pack.close.recount')}</Banner>
                <Field label={t('pack.close.recounted')}>
                  <YesNo value={recounted ? true : null} onChange={setRecounted} />
                </Field>
              </>
            )}
          </CardBody>
        </Card>
      )}
      <Card>
        <CardBody className="py-1">
          <Line label={t('pack.close.weightIn')} value={fmt.weight(inputG)} />
          <Line label={t('pack.close.leftover')} value={fmt.weight(leftoverG)} />
          <Line label={t('pack.close.spillage')} value={fmt.weight(spillageG)} />
          <p className="pb-2 text-[12px] text-muted">{t('pack.close.balanceAfterSync')}</p>
        </CardBody>
      </Card>

      <Button size="xl" fullWidth disabled={!ready} loading={saving} onClick={() => void close()}>
        <BilingualLabel k="process.close.button" />
      </Button>
      <Button variant="ghost" size="touch" onClick={() => update((d) => ({ ...d, finished: false }))}>
        {t('pack.close.backToFill')}
      </Button>
    </div>
  )
}
